import styled from '@emotion/styled'
import { WorksheetDomain } from '@mathflat/domain/@entities'
import { AxiosError } from 'axios'
import { debounce } from 'lodash-es'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'

import { type AutoScoringRequest, RequestScoring, studentWorksheetApi } from '~/@common/api'
import { routeName } from '~/@common/constants'
import { useLearningTime } from '~/@common/hooks/useLearningTime'
import { useRepository } from '~/@common/hooks/useRepository'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import ProblemScoringViewGuideOption from '~/@pages/@common/(ProblemScoring)/ProblemScoringViewGuideOption'
import 전체정답_전체삭제_제출 from '~/@pages/@common/(ProblemScoring)/전체정답_전체삭제_제출'
import { WorksheetDifficultyStatistics } from '~/@pages/@common/(QuickScoring)/ParentReportCards/WorksheetDifficultyStatistics'
import { WorksheetSupplementaryLearning } from '~/@pages/@common/(QuickScoring)/ParentReportCards/WorksheetSupplementaryLearning'
import { QuickScoringTemplate } from '~/@pages/@common/(QuickScoring)/QuickScoring.template'
import { openUnavailableAlertMessageModal } from '~/@pages/@widgets/(Worksheet)/접근권한모달'
import { ScoringSubmitConfirmModal } from '~/@pages/student/@widgets/StudentWorksheetScoring/ScoringSubmitConfirmModal'

import { StudentWorksheetDetailPageService } from '../@service/StudentWorksheetDetailPage.service'
import { StudentWorksheetHeader } from '../@widgets/StudentWorksheetHeader'
import { alertClearAllAnswerModal } from './@widgets/alertClearAllAnswerModal'
import {
  alertWorksheetExceptionErrorModal,
  ExceptionModal,
} from './alertWorksheetExceptionErrorModal'

/**
 * 학습지 채점 페이지.
 *
 * 학생 / 학부모 컨텍스트는 service.problemScoringViewOption.isParentView 로 분기.
 * 분기 신호 = `CUSTOM_WORKSHEET_WRITE` restriction (서비스에서 결정).
 *
 * 학부모 모드일 때 끄는 동작:
 *   - cheat 트래킹
 *   - 학습시간(useLearningTime)
 *   - 답안 입력 자동저장(virtual scoring)
 *   - 전체정답 / 전체삭제 / 제출 footer
 *   - 정답해설/문풀동 노출 정책은 viewOption getter 가 자동 derive (학부모정답해설공개 만 적용)
 */
const StudentWorksheetScoringPage = observer(() => {
  const { studentWorksheetId } = useParams<{ studentWorksheetId: string }>()
  const tempLocalUserInputRef = useRef<
    Map<
      AutoScoringRequest<'WORKSHEET', '일반'>['worksheetProblemId'],
      AutoScoringRequest<'WORKSHEET', '일반'>
    >
  >(new Map())
  // TODO: /student-worksheet 나갈 때 dynamicRepo.del 시킬 수 있도록, student-worksheet을 감싸는 페이지 컴포넌트 신설 필요
  const service = useRepository([StudentWorksheetDetailPageService, studentWorksheetId!])

  // 부모/학생 SSOT — service.problemScoringViewOption?.isParentView 가 SSOT.
  // hydrate 전 undefined 인 동안에는 commonRepo.permission.isParent (userRole) 로 즉시 fallback
  // 해서 학생 트래킹이 첫 진입에 누락되지 않도록 함.
  const isParentView =
    service.problemScoringViewOption?.isParentView ?? commonRepo.permission.isParent
  const isStudentView = !isParentView

  // 학부모는 학생 학습시간으로 카운팅되면 안 됨 → enabled = false
  useLearningTime(service.worksheet?.type, service.studentWorksheet?.status, isStudentView)

  const navigate = useNavigate()

  const confirmToSubmitAnswers = async () => {
    modalService.openModal(
      <ScoringSubmitConfirmModal
        onClick={async () => {
          if (!studentWorksheetId) {
            return
          }
          try {
            modalService.showLoader()
            modalService.closeModal()
            if (service.worksheet?.type === WorksheetDomain.TYPE.오늘의학습) {
              await service.onSubmitDailyWorksheetProblems(studentWorksheetId)
            } else {
              await service.onSubmitWorksheetProblems(studentWorksheetId)
            }
          } catch (error) {
            if (error instanceof Error) {
              // CASE: 학습지가 삭제되거나 수정된 경우
              if (
                error.message === 'WORKSHEET_ALREADY_DELETED' ||
                error.message === 'STUDENT_WORKSHEET_NOT_FOUND'
              ) {
                alertWorksheetExceptionErrorModal()
              } else if (error.message === 'WORKSHEET_PERMISSION_DENIED') {
                openUnavailableAlertMessageModal()
              } else {
                // CASE: 학습지에 대한 접근 권한이 없을 때
                openUnavailableAlertMessageModal()
              }
            }
          } finally {
            modalService.hideLoader()
          }
        }}
      />,
      {
        modalName: 'ScoringSubmitConfirmModal',
      },
    )
  }

  // 학부모는 트래킹 대상 아님 — 권한 hydrate 전 undefined 상태에서도 호출 보류
  useEffect(() => {
    if (!isStudentView) return
    ;(async () => {
      if (commonRepo.studentId && studentWorksheetId) {
        try {
          await studentWorksheetApi.postStudentCheat(commonRepo.studentId, studentWorksheetId)
        } catch (error) {
          if (error instanceof AxiosError) {
            if (error.response?.data?.code === 'STUDENT_WORKSHEET_NOT_FOUND') {
              modalService.closeModal()
              modalService.openModal(<ExceptionModal />, {
                modalName: '삭제&수정_학습지_안내',
              })
            }
          }
        }
      }
    })()
  }, [studentWorksheetId, isStudentView])

  useEffect(() => {
    if (studentWorksheetId) {
      ;(async () => {
        try {
          await service.loadAndSetStudentWorksheetScorings(+studentWorksheetId)
        } catch (error) {
          if (error instanceof Error) {
            if (error.message === 'STUDENT_WORKSHEET_NOT_FOUND') {
              modalService.closeModal()
              modalService.openModal(<ExceptionModal />, {
                modalName: '삭제&수정_학습지_안내',
              })
            }
          }
        }
      })()
    }
  }, [service, studentWorksheetId])

  useEffect(() => {
    if (!studentWorksheetId) {
      navigate(isParentView ? routeName.parent.defaultPath : routeName.student.studentWorksheet)
    }
  }, [studentWorksheetId, isParentView])

  const onVirtualScoringBatchSubmit = useCallback(async () => {
    // 전송 스냅샷 생성 (전송 중 추가 입력 보존)
    const snapshot = Array.from(tempLocalUserInputRef.current.entries())

    if (snapshot.length === 0) return

    const idNum = Number(studentWorksheetId)

    if (!studentWorksheetId || Number.isNaN(idNum)) return

    try {
      const payload = snapshot.map(([, v]) => v)
      await service.onVirtualScoringSubmitById(idNum, payload)

      // 전송 당시 값과 동일한 항목만 제거 (전송 중 수정된 값은 보존)
      for (const [k, sent] of snapshot) {
        const cur = tempLocalUserInputRef.current.get(k)
        if (cur && JSON.stringify(cur) !== JSON.stringify(sent)) continue
        tempLocalUserInputRef.current.delete(k)
      }
    } catch (error) {
      console.error('Virtual scoring submit failed:', error)
    }
  }, [service, studentWorksheetId])

  const onDebouncedVirtualScoringBatchSubmit = useCallback(
    debounce(onVirtualScoringBatchSubmit, 1000),
    [onVirtualScoringBatchSubmit],
  )

  const _onVirtualScoringSubmit = (
    localUserInput: RequestScoring.자동채점<'WORKSHEET'> | RequestScoring.일반채점<'WORKSHEET'>,
  ) => {
    // 학부모는 입력/저장 자체가 없으므로 도달 안 함. 안전상 가드 (undefined 도 student 아님 처리).
    if (!isStudentView) return
    // LIVE_LESSON 모드는 service 가 LOCAL_STORAGE 로 결정 — 서버 호출 차단
    if (service.scoringSaveStorage !== 'SERVER') return
    if (!service.worksheet?.autoScorable || !(localUserInput instanceof RequestScoring.자동채점)) {
      return
    }
    const params = RequestScoring.자동채점.toRequestParams<'WORKSHEET', '일반'>(localUserInput)

    tempLocalUserInputRef.current.set(params.worksheetProblemId, params)
    onDebouncedVirtualScoringBatchSubmit()
  }

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 debounced 함수 취소하고 남은 데이터 즉시 전송
      onDebouncedVirtualScoringBatchSubmit.cancel()
      if (tempLocalUserInputRef.current.size > 0) {
        onVirtualScoringBatchSubmit()
      }
    }
  }, [onDebouncedVirtualScoringBatchSubmit, onVirtualScoringBatchSubmit])

  return (
    <S.StudentWorksheetScoringPage>
      <StudentWorksheetHeader title={service.worksheet?.title} />
      <ProblemScoringViewGuideOption viewOption={service.problemScoringViewOption} />
      <QuickScoringTemplate
        type="WORKSHEET"
        contentId={studentWorksheetId}
        viewOption={service.problemScoringViewOption}
        problemScoringColl={service.problemScoringColl}
        score={service.studentWorksheet?.score ?? 0}
        dashboardSlot={
          isParentView ? (
            <WorksheetDifficultyStatistics
              studentId={commonRepo.studentId}
              worksheetId={service.worksheet?.id}
            />
          ) : undefined
        }
        supplementaryLearningSlot={
          isParentView ? (
            <WorksheetSupplementaryLearning
              studentId={commonRepo.studentId}
              worksheetId={service.worksheet?.id}
            />
          ) : undefined
        }
        onLocalUserInputChange={isStudentView ? _onVirtualScoringSubmit : undefined}
        scoring={{
          ...{ total: 0, correct: 0, incorrect: 0, unknown: 0, none: 0 },
          ...service.problemScoringColl?.gradingCount,
          total: service.problemScoringColl?.toArr.length || 0,
        }}
      />
      {isStudentView && !service.problemScoringViewOption?.채점완료_혹은_채점불가능 && (
        <전체정답_전체삭제_제출
          className="flex-end"
          onAllClearClick={() => {
            alertClearAllAnswerModal({
              onAllClear: async () => {
                service.problemScoringColl?.onAllClear()
                await service.onDeleteVirtualScoring(Number(studentWorksheetId))
              },
            })
          }}
          onSubmitClick={() => {
            if (!service.problemScoringColl?.toScoredArr.length) {
              return
            }
            confirmToSubmitAnswers()
          }}
          submitTitle={
            service.problemScoringColl
              ? `${service.problemScoringColl?.toScoredArr.length}문제 제출`
              : ''
          }
          {...(!service.worksheet?.autoScorable && {
            onAllCorrectClick: () => {
              service.problemScoringColl?.onAllCheckCorrect()
            },
          })}
        />
      )}
    </S.StudentWorksheetScoringPage>
  )
})

export default StudentWorksheetScoringPage

const S = {
  StudentWorksheetScoringPage: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  `,
}
